import { DynamicWidgetsProvided, RefinementListProvided } from 'react-instantsearch-core';
import { connectDynamicWidgets, connectRefinementList } from 'react-instantsearch-dom';

import React, { forwardRef, memo, useMemo, useState } from 'react';
import { useTranslation } from '~/shared/utils/translation';
import { CollapseHeader, CollapsePanel } from '~/shared/components';
import { FilterGroupAttributes } from '../../FiltersGroup';
import { TranslationKey } from '~/lib/data-contract';
import styles from './drawer-filters.module.css';

interface SingleFilterItemProps extends RefinementListProvided {
    filterName: string;
    title: string;
    index: number;
    isOpen: boolean;
    setOpen: (filterName: string | null) => void;
}

const SingleFilterItem = memo(
    forwardRef<HTMLDivElement, SingleFilterItemProps>(
        ({ filterName, title, items, isOpen, setOpen }, ref) => {
            const { translate } = useTranslation();

            // If we have no filters at all in this group, abort at once.
            if (!items.length) return <></>;

            return (
                <div className={styles.item} ref={ref}>
                    <CollapseHeader
                        className={styles.paddings}
                        isOpen={isOpen}
                        onClick={() => setOpen(isOpen ? null : filterName)}
                        textVariant="body"
                        as="div"
                    >
                        {translate(title as TranslationKey)}
                    </CollapseHeader>
                    <CollapsePanel isOpen={isOpen}>
                        <div className={styles.paddings}>
                            <FilterGroupAttributes
                                key={filterName}
                                attribute={filterName}
                                showMore
                                showMoreLimit={100}
                            />
                        </div>
                    </CollapsePanel>
                </div>
            );
        }
    )
);
const ConnectedSingleFilterItem = connectRefinementList(SingleFilterItem);
ConnectedSingleFilterItem.displayName = 'ConnectedSingleFilterItem';

export const DrawerFiltersComponent = memo(({ attributesToRender }: DynamicWidgetsProvided) => {
    const [openFilter, setOpenFilter] = useState<string | null>(null);
    const filters = useMemo(() => {
        return attributesToRender.map((filter) => {
            return {
                name: filter,
                title: `commerce.filters.${filter}`,
            };
        });
    }, [attributesToRender]);

    return (
        <div>
            {filters.map(({ name, title }: { name: string; title: string }, index) => (
                <ConnectedSingleFilterItem
                    isOpen={openFilter === name}
                    setOpen={setOpenFilter}
                    attribute={name}
                    key={name}
                    {...{ filterName: name, title, index }}
                />
            ))}
        </div>
    );
});

export const DrawerFilters = connectDynamicWidgets(DrawerFiltersComponent);
